import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi, type WaveGridResponse } from '../api'
import { useViewCtx } from '../context/ViewContext'
import { gridHasData } from '../maps/gridEmpty'
import { toContourGrid } from '../maps/isothermMath'
import { overlayGridKey } from './overlayLayers'
import { buildWaveContours, buildWaveLabels, waveLevelsForStep, gridMaxHs } from './waveContours'
import { collectionDrawn } from './drawn'
import { toolPending } from './toolPending'
import type { ToolsView } from './seriesOverride'

/**
 * Изо-линии высоты волны на карте раздела (оверлей «Волнение»). Общий над
 * «Картами» и «Аналитикой»: сетку волнения (реальный VHM0 где есть, иначе оценка
 * по ветру) грузим на вьюпорт (и перечитываем по завершении докачки — `loadNonce`,
 * см. overlayGridKey), контуры считает браузер (marchingSquares) и красит их тем
 * же градиентом, что и стрелки. Возвращает источник ('cmems'|'wind'|null) для
 * честной подписи легенды.
 */
export function useWaveIso(
  mapRef: RefObject<MapCanvasHandle | null>,
  viewKey: string,
  loadNonce = 0,
  /** Значения кадра серии; null — вид пользователя (см. `seriesOverride`). */
  override: ToolsView | null = null,
): {
  source: 'cmems' | 'wind' | null
  hasData: boolean
  drawn: boolean
  /** Поле ещё не отвечает на текущий запрос — кадр серии снимать рано. */
  pending: boolean
} {
  const ctx = useViewCtx()
  const date = ctx.date
  const waveOverlay = override ? override.waveOverlay : ctx.waveOverlay
  const waveStep = override ? override.waveStep : ctx.waveStep
  const [grid, setGrid] = useState<WaveGridResponse | null>(null)
  // Поле может быть полным, а изолиний — ни одной: при волне 1,5…2,0 м и шаге
  // 0,5 м уровень 2,0 задевает лишь пятно, остальные не пересекают поле вовсе.
  const [drawn, setDrawn] = useState(true)
  // Под какой запрос лежит поле (см. toolPending).
  const [loadedKey, setLoadedKey] = useState<string | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map || !waveOverlay) {
      setGrid(null)
      setLoadedKey(null)
      return
    }
    let alive = true
    // Дебаунс: в «Аналитике» ползунок дней меняет date на каждый шаг.
    const id = setTimeout(() => {
      const key = overlayGridKey({ date, viewKey, loadNonce })
      cabinetApi
        .wavesGrid({ date, bbox: map.getBBox() })
        .then(g => {
          if (!alive) return
          setGrid(g)
          setLoadedKey(key)
        })
        // Отказ — тоже ответ: иначе серия ждала бы поле, которого не будет.
        .catch(() => {
          if (!alive) return
          setGrid(null)
          setLoadedKey(key)
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveOverlay, overlayGridKey({ date, viewKey, loadNonce })])

  // Смена шага (waveStep) НЕ ходит в сеть — те же данные, только другой набор
  // уровней: контуры пересчитывает браузер по кэшированной сетке (как изотермы).
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const g = waveOverlay ? toContourGrid(grid) : null
    if (!g) {
      map.removeWaveContours('wave')
      setDrawn(true)
      return
    }
    const levels = waveLevelsForStep(waveStep, gridMaxHs(g))
    const contours = buildWaveContours(g, levels)
    setDrawn(collectionDrawn(contours))
    map.setWaveContours('wave', contours, buildWaveLabels(g, levels))
  }, [grid, waveOverlay, waveStep])

  useEffect(
    () => () => {
      mapRef.current?.removeWaveContours('wave')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    source: waveOverlay ? grid?.meta?.wave_source ?? 'wind' : null,
    // Сетка приходит нужного размера и вся из дыр, когда поля за дату нет.
    hasData: gridHasData(grid),
    drawn,
    pending: toolPending(loadedKey, overlayGridKey({ date, viewKey, loadNonce }), waveOverlay),
  }
}
